import { useMemo, useState } from 'react';
import type { Novel } from '../../data/samples';
import { NovelOrderArraySchema } from '../../utils/validation/storageSchemas';

export type BookshelfFilter = 'all' | 'presets' | 'my-novels';
export type BookshelfStatusFilter = 'all' | 'ongoing' | 'completed' | 'short';
export type BookshelfSortOrder = 'recent' | 'title' | 'manual';

export const useBookshelfFilters = (novels: Novel[], customNovels: Novel[] = []) => {
  const [filter, setFilter] = useState<BookshelfFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGenre, setSelectedGenre] = useState<string>('all');
  const [selectedTag, setSelectedTag] = useState<string>('all');
  const [selectedStatus, setSelectedStatus] = useState<BookshelfStatusFilter>('all');
  const [sortOrder, setSortOrder] = useState<BookshelfSortOrder>(() => {
    // デフォルトで手動並び替えが保存されていれば manual にする
    const stored = localStorage.getItem('novelflow_novel_order');
    return stored ? 'manual' : 'recent';
  });
  const [manualOrder, setManualOrder] = useState<string[]>(() => {
    try {
      const stored = localStorage.getItem('novelflow_novel_order');
      if (!stored) return [];
      const parsed = JSON.parse(stored);
      const result = NovelOrderArraySchema.safeParse(parsed);
      if (result.success) {
        return result.data;
      }
      console.warn('Failed to parse manual order:', result.error);
      return [];
    } catch {
      return [];
    }
  });

  const allGenres = useMemo(() => {
    const genres = new Set<string>();
    for (const n of novels) {
      if (n.genre) genres.add(n.genre);
    }
    return ['all', ...Array.from(genres)];
  }, [novels]);

  const allTags = useMemo(() => {
    const tags = new Set<string>();
    for (const n of novels) {
      if (n.tags && Array.isArray(n.tags)) {
        for (const t of n.tags) {
          tags.add(t);
        }
      }
    }
    return ['all', ...Array.from(tags)];
  }, [novels]);

  const customNovelIds = useMemo(
    () => new Set(customNovels.map((novel) => novel.id)),
    [customNovels],
  );
  const customNovelOrder = useMemo(
    () => new Map(customNovels.map((novel, index) => [novel.id, index])),
    [customNovels],
  );
  const originalNovelOrder = useMemo(
    () => new Map(novels.map((novel, index) => [novel.id, index])),
    [novels],
  );

  const filteredNovels = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase('ja');
    const filtered = novels.filter((novel) => {
      const isCustomNovel = customNovelIds.has(novel.id);
      if (filter === 'presets' && isCustomNovel) return false;
      if (filter === 'my-novels' && !isCustomNovel) return false;

      if (normalizedQuery) {
        const searchableText = [
          novel.title,
          novel.author,
          novel.description,
          novel.genre,
          ...(novel.tags ?? []),
        ]
          .filter(Boolean)
          .join(' ')
          .toLocaleLowerCase('ja');
        if (!searchableText.includes(normalizedQuery)) return false;
      }

      if (selectedGenre !== 'all' && novel.genre !== selectedGenre) return false;

      if (selectedTag !== 'all' && (!novel.tags || !novel.tags.includes(selectedTag))) return false;

      if (selectedStatus !== 'all') {
        const status = novel.status || 'ongoing';
        if (status !== selectedStatus) return false;
      }

      return true;
    });

    return filtered.sort((a, b) => {
      if (sortOrder === 'title') {
        return a.title.localeCompare(b.title, 'ja');
      }
      if (sortOrder === 'manual') {
        const indexA = manualOrder.indexOf(a.id);
        const indexB = manualOrder.indexOf(b.id);
        if (indexA !== -1 && indexB !== -1) {
          return indexA - indexB;
        }
        if (indexA !== -1) return -1;
        if (indexB !== -1) return 1;
        return 0;
      }

      const customIndexA = customNovelOrder.get(a.id);
      const customIndexB = customNovelOrder.get(b.id);
      if (customIndexA !== undefined && customIndexB !== undefined) {
        const timestampA = Date.parse(a.lastUpdatedAt || a.downloadedAt || '');
        const timestampB = Date.parse(b.lastUpdatedAt || b.downloadedAt || '');
        if (
          Number.isFinite(timestampA) &&
          Number.isFinite(timestampB) &&
          timestampA !== timestampB
        ) {
          return timestampB - timestampA;
        }
        return customIndexA - customIndexB;
      }
      if (customIndexA !== undefined) return -1;
      if (customIndexB !== undefined) return 1;
      return (originalNovelOrder.get(a.id) ?? 0) - (originalNovelOrder.get(b.id) ?? 0);
    });
  }, [
    novels,
    customNovelIds,
    customNovelOrder,
    originalNovelOrder,
    filter,
    searchQuery,
    selectedGenre,
    selectedTag,
    selectedStatus,
    sortOrder,
    manualOrder,
  ]);

  const resetSubFilters = () => {
    setSelectedGenre('all');
    setSelectedTag('all');
    setSelectedStatus('all');
  };

  const updateManualOrder = (newOrder: string[]) => {
    setManualOrder(newOrder);
    try {
      localStorage.setItem('novelflow_novel_order', JSON.stringify(newOrder));
    } catch (e) {
      console.error('Failed to save manual order', e);
    }
  };

  return {
    filter,
    setFilter,
    searchQuery,
    setSearchQuery,
    selectedGenre,
    setSelectedGenre,
    selectedTag,
    setSelectedTag,
    selectedStatus,
    setSelectedStatus,
    sortOrder,
    setSortOrder,
    allGenres,
    allTags,
    filteredNovels,
    resetSubFilters,
    manualOrder,
    updateManualOrder,
  };
};
